import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
  Box,
  Divider,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useMemo, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InternShopifyOrdersCardQuery } from "./__generated__/InternShopifyOrdersCardQuery.graphql";
import { useColorMode } from "../../../contexts/ColorModeContext";
import formatNumber from "../../../utils/formatNumber";
import { InternPanel } from "../components";

const orderMetricsQuery = graphql`
  query InternShopifyOrdersCardQuery($brandId: ID!, $days: Int!) {
    brand(id: $brandId) {
      orderMetricsSummary(days: $days) {
        dailyStats {
          date
          totalOrders
          kinnInvolvedOrders
          kinnOrders
        }
        totalOrders
        kinnInvolvedOrders
        kinnOrders
        involvementRate
        attributionRate
      }
    }
  }
`;

type TimeRange = "1" | "7" | "30";

type Props = {
  brandId: string;
};

const InternShopifyOrdersCard = ({ brandId }: Props) => {
  const { colorMode } = useColorMode();
  const [timeRange, setTimeRange] = useState<TimeRange>("7");

  const data = useLazyLoadQuery<InternShopifyOrdersCardQuery>(
    orderMetricsQuery,
    {
      brandId,
      days: parseInt(timeRange, 10),
    }
  );

  const orderMetrics = data.brand?.orderMetricsSummary;
  const attributionRate = orderMetrics?.attributionRate ?? 0;
  const involvementRate = orderMetrics?.involvementRate ?? 0;

  // Prepare chart data
  const chartData = useMemo(() => {
    const stats = orderMetrics?.dailyStats ?? [];
    return stats.map((stat) => ({
      date: new Date(stat.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      total: stat.totalOrders,
      involved: stat.kinnInvolvedOrders,
      attributed: stat.kinnOrders,
    }));
  }, [orderMetrics?.dailyStats]);

  // Theme-aware colors
  const chartColors = {
    grid: colorMode === "dark" ? "#374151" : "#e5e7eb",
    text: colorMode === "dark" ? "#9ca3af" : "#6b7280",
    tooltip: {
      bg: colorMode === "dark" ? "#1f2937" : "#ffffff",
      border: colorMode === "dark" ? "#374151" : "#e5e7eb",
    },
  };

  const hasData = (orderMetrics?.totalOrders ?? 0) > 0;

  const handleTimeRangeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newValue: TimeRange | null) => {
      if (newValue) {
        setTimeRange(newValue);
      }
    },
    []
  );

  const timeLabel =
    timeRange === "1" ? "Today" : timeRange === "7" ? "7d" : "30d";

  return (
    <InternPanel
      title="Shopify Order Attribution"
      subtitle="Order funnel: Total → Kinn Involved → Attributed"
      icon={<LocalShippingIcon />}
      collapsible={false}
      actions={
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          size="small"
        >
          <ToggleButton
            value="1"
            sx={{ px: 1.5, py: 0.25, fontSize: "0.6875rem" }}
          >
            Today
          </ToggleButton>
          <ToggleButton
            value="7"
            sx={{ px: 1.5, py: 0.25, fontSize: "0.6875rem" }}
          >
            7d
          </ToggleButton>
          <ToggleButton
            value="30"
            sx={{ px: 1.5, py: 0.25, fontSize: "0.6875rem" }}
          >
            30d
          </ToggleButton>
        </ToggleButtonGroup>
      }
    >
      <Stack spacing={2}>
        {/* Rate Progress Bars */}
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: "0.6875rem",
              }}
            >
              Involvement Rate ({timeLabel})
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 600,
                fontSize: "0.6875rem",
              }}
            >
              {involvementRate.toFixed(1)}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(involvementRate, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "var(--intern-bg-tertiary)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                bgcolor: "#f59e0b",
              },
            }}
          />
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: "0.6875rem",
              }}
            >
              Attribution Rate ({timeLabel})
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 600,
                fontSize: "0.6875rem",
              }}
            >
              {attributionRate.toFixed(1)}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(attributionRate, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "var(--intern-bg-tertiary)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                bgcolor: "#10b981",
              },
            }}
          />
        </Box>

        {/* Stats Row - 3 columns */}
        <Stack
          direction="row"
          spacing={0}
          sx={{
            bgcolor: "var(--intern-bg-secondary)",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              textAlign: "center",
              borderRight: "1px solid var(--intern-border-default)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.625rem",
                display: "block",
                mb: 0.5,
              }}
            >
              Total Orders
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {formatNumber(orderMetrics?.totalOrders ?? 0)}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              textAlign: "center",
              borderRight: "1px solid var(--intern-border-default)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.625rem",
                display: "block",
                mb: 0.5,
              }}
            >
              Kinn Involved
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "#f59e0b",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {formatNumber(orderMetrics?.kinnInvolvedOrders ?? 0)}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.625rem",
                display: "block",
                mb: 0.5,
              }}
            >
              Attributed
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "#10b981",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {formatNumber(orderMetrics?.kinnOrders ?? 0)}
            </Typography>
          </Box>
        </Stack>

        <Divider />

        {/* Chart */}
        {hasData ? (
          <Box sx={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartColors.grid}
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  stroke={chartColors.text}
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: chartColors.text }}
                />
                <YAxis
                  stroke={chartColors.text}
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: chartColors.text }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: chartColors.tooltip.bg,
                    border: `1px solid ${chartColors.tooltip.border}`,
                    borderRadius: 8,
                    fontSize: "0.75rem",
                  }}
                  formatter={(value: number, name: string) => {
                    const labels: Record<string, string> = {
                      total: "Total Orders",
                      involved: "Kinn Involved",
                      attributed: "Attributed",
                    };
                    return [value, labels[name] || name];
                  }}
                  labelStyle={{ color: chartColors.text }}
                />
                <Legend
                  iconType="square"
                  iconSize={8}
                  formatter={(value) => {
                    const labels: Record<string, string> = {
                      total: "Total",
                      involved: "Involved",
                      attributed: "Attributed",
                    };
                    return (
                      <span
                        style={{
                          color: chartColors.text,
                          fontSize: "0.6875rem",
                        }}
                      >
                        {labels[value] || value}
                      </span>
                    );
                  }}
                />
                <Bar dataKey="total" fill="#6366f1" radius={[2, 2, 0, 0]} />
                <Bar dataKey="involved" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                <Bar
                  dataKey="attributed"
                  fill="#10b981"
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              height: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "var(--intern-text-tertiary)" }}
            >
              No order data available yet
            </Typography>
          </Box>
        )}
      </Stack>
    </InternPanel>
  );
};

export default InternShopifyOrdersCard;
